import React, { useState, useMemo } from "react";
import { X, MapPinned, UserCog, Check, Loader2 } from "lucide-react";

const DIAS_SHORT = ["SEG", "TER", "QUA", "QUI", "SEX", "SAB", "DOM"];

const KEEP = "__KEEP__";
const REMOVE = "__REMOVE__";

export default function BulkAssignSheet({ open, count, rotas, cobradores, onCancel, onApply, applying }) {
  const [rota, setRota] = useState(KEEP);
  const [cobrador, setCobrador] = useState(KEEP);
  const [confirm, setConfirm] = useState(false);

  const reset = () => { setRota(KEEP); setCobrador(KEEP); setConfirm(false); };
  const close = () => { reset(); onCancel(); };

  const changed = rota !== KEEP || cobrador !== KEEP;

  const resumo = useMemo(() => {
    const parts = [];
    if (rota !== KEEP) {
      if (rota === REMOVE) parts.push(<><b>remover rota</b></>);
      else {
        const r = rotas.find((x) => x.id === rota);
        if (r) parts.push(<>atribuir rota <b>{r.nome} ({DIAS_SHORT[r.dia_semana]})</b></>);
      }
    }
    if (cobrador !== KEEP) {
      if (cobrador === REMOVE) parts.push(<><b>remover cobrador</b></>);
      else {
        const c = cobradores.find((x) => x.id === cobrador);
        if (c) parts.push(<>atribuir cobrador <b>{c.name}</b></>);
      }
    }
    return parts;
  }, [rota, cobrador, rotas, cobradores]);

  const apply = () => {
    const payload = { set_rota: false, set_cobrador: false, rota_id: null, assigned_cobrador_id: null };
    if (rota !== KEEP) {
      payload.set_rota = true;
      payload.rota_id = rota === REMOVE ? null : rota;
    }
    if (cobrador !== KEEP) {
      payload.set_cobrador = true;
      payload.assigned_cobrador_id = cobrador === REMOVE ? null : cobrador;
    }
    onApply(payload, () => reset());
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40" data-testid="bulk-assign-overlay" onClick={close}>
      <div
        className="w-full max-w-md bg-[color:var(--cream)] rounded-t-3xl p-5 shadow-2xl animate-slide-up"
        data-testid="bulk-assign-sheet"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="font-display text-xl font-extrabold">Atribuir em massa</h2>
            <p className="text-xs text-stone-500 mt-0.5">{count} cliente{count > 1 ? "s" : ""} selecionado{count > 1 ? "s" : ""}</p>
          </div>
          <button data-testid="bulk-close" onClick={close} className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-stone-500 inline-flex items-center gap-1">
              <MapPinned className="w-3 h-3" /> Rota de cobrança
            </label>
            <select
              data-testid="bulk-rota-select"
              value={rota}
              onChange={(e) => setRota(e.target.value)}
              className="mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 bg-white"
            >
              <option value={KEEP}>— Não mexer —</option>
              <option value={REMOVE}>— Remover rota —</option>
              {rotas.map((r) => (
                <option key={r.id} value={r.id}>{r.nome} ({DIAS_SHORT[r.dia_semana]})</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-stone-500 inline-flex items-center gap-1">
              <UserCog className="w-3 h-3" /> Cobrador responsável
            </label>
            <select
              data-testid="bulk-cobrador-select"
              value={cobrador}
              onChange={(e) => setCobrador(e.target.value)}
              className="mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 bg-white"
            >
              <option value={KEEP}>— Não mexer —</option>
              <option value={REMOVE}>— Remover cobrador —</option>
              {cobradores.map((c) => (
                <option key={c.id} value={c.id}>{c.name} (@{c.username})</option>
              ))}
            </select>
          </div>
        </div>

        {confirm && changed && (
          <div className="mt-4 p-3 rounded-xl bg-[color:var(--terracotta-light)] border border-[color:var(--terracotta)]/30 text-sm text-stone-800" data-testid="bulk-confirm-box">
            Vou {resumo.map((r, i) => (<span key={i}>{i > 0 ? " e " : ""}{r}</span>))} para <b>{count} cliente{count > 1 ? "s" : ""}</b>. Confirma?
          </div>
        )}

        <div className="mt-4 grid grid-cols-2 gap-2">
          {!confirm ? (
            <>
              <button data-testid="bulk-cancel" onClick={close} className="h-12 rounded-xl border border-stone-300 font-bold text-stone-700">
                Cancelar
              </button>
              <button
                data-testid="bulk-review"
                disabled={!changed}
                onClick={() => setConfirm(true)}
                className="btn-primary disabled:opacity-40"
              >
                Revisar
              </button>
            </>
          ) : (
            <>
              <button data-testid="bulk-edit-back" onClick={() => setConfirm(false)} className="h-12 rounded-xl border border-stone-300 font-bold text-stone-700">
                Voltar
              </button>
              <button
                data-testid="bulk-apply"
                disabled={applying}
                onClick={apply}
                className="btn-primary"
              >
                {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {applying ? "Aplicando..." : "Confirmar"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
